const STYLE_FILTERS = ['printed', 'embroidered', 'plain'];

/**
 * @param {string} itemStyle - Handleized style string from data-swatch-style
 * @returns {string | null} Matching style filter handle, or null if none match
 */
function resolveStyleFromItemStyle(itemStyle) {
  for (const style of STYLE_FILTERS) {
    if (itemStyle.includes(style)) return style;
  }

  return null;
}

if (!customElements.get('swatch-tray')) {
  customElements.define(
    'swatch-tray',
    class extends HTMLElement {
      constructor() {
        super();

        /** @type {string | null} */
        this.activeStyle = null;

        /** @type {string | null} */
        this.activeMotif = null;

        /** @type {string | null} */
        this.preferredStyle = null;

        /** @type {string | null} */
        this.preferredMotif = null;

        /** @type {(() => void) | null} */
        this.boundUpdateIndicators = null;

        /** @type {((e: Event) => void) | null} */
        this.boundOnModalActivate = null;

        /** @type {((e: Event) => void) | null} */
        this.boundOnModalDeactivate = null;

        /** @type {HTMLElement | null} */
        this.scrollRoot = null;

        /** @type {HTMLElement | null} */
        this.stickyFilters = null;

        /** @type {(() => void) | null} */
        this.boundUpdateStickyFilters = null;

        /** @type {(() => void) | null} */
        this.boundUpdateMotifScrollHints = null;

        /** @type {((e: TransitionEvent) => void) | null} */
        this.boundStickyTransitionEnd = null;
      }

      connectedCallback() {
        this.filters = this.querySelector('[data-swatch-filters]');
        this.motifFilters = this.querySelector('[data-swatch-motif-filters]');
        this.indicator = this.querySelector('[data-swatch-indicator]');
        this.items = this.querySelectorAll('[data-swatch-item]');
        this.buttons = this.querySelectorAll('[data-swatch-style-filter]');
        this.motifButtons = this.querySelectorAll('[data-swatch-motif-filter]');
        this.status = this.querySelector('[data-swatch-status]');
        this.trigger = document.querySelector('[data-focus-trap-toggle="swatch-tray"]:not([data-focus-trap-fallback])');
        this.preferredStyle = this.getAttribute('data-swatch-active-style');
        this.preferredMotif = this.getAttribute('data-swatch-active-motif');

        this.resetToCurrentProductFilters({ animate: false, announce: false });

        this.filters?.addEventListener('click', (e) => {
          const target = e.target;
          if (!(target instanceof Element)) return;

          const button = target.closest('[data-swatch-style-filter]');
          if (!(button instanceof HTMLButtonElement)) return;

          this.toggleFilter(button);
        });

        this.motifFilters?.addEventListener('click', (e) => {
          const target = e.target;
          if (!(target instanceof Element)) return;

          const button = target.closest('[data-swatch-motif-filter]');
          if (!(button instanceof HTMLButtonElement) || button.hidden) return;

          this.toggleMotifFilter(button);
        });

        this.boundUpdateMotifScrollHints = () => this.updateMotifScrollHints();
        this.motifFilters?.addEventListener('scroll', this.boundUpdateMotifScrollHints, { passive: true });

        this.boundUpdateIndicators = () => {
          this.updateIndicator({ animate: false });
          this.updateMotifScrollHints();
        };
        this.boundOnModalActivate = (e) => {
          if (!(e instanceof CustomEvent)) return;
          if (e.detail?.id !== 'swatch-tray') return;

          this.setTriggerExpanded(true);
          this.resetToCurrentProductFilters({ animate: false, announce: false });
          this.bindStickyFilters();
        };
        this.boundOnModalDeactivate = (e) => {
          if (!(e instanceof CustomEvent)) return;
          if (e.detail?.id !== 'swatch-tray') return;

          this.setTriggerExpanded(false);
          this.unbindStickyFilters();
        };

        window.addEventListener('resize', this.boundUpdateIndicators);
        window.addEventListener('modal-trap-activate', this.boundOnModalActivate);
        window.addEventListener('modal-trap-deactivate', this.boundOnModalDeactivate);
      }

      disconnectedCallback() {
        this.unbindStickyFilters();
        this.clearMotifScrollHints();

        if (this.motifFilters && this.boundUpdateMotifScrollHints) {
          this.motifFilters.removeEventListener('scroll', this.boundUpdateMotifScrollHints);
        }

        if (this.boundUpdateIndicators) {
          window.removeEventListener('resize', this.boundUpdateIndicators);
        }

        if (this.boundOnModalActivate) {
          window.removeEventListener('modal-trap-activate', this.boundOnModalActivate);
        }

        if (this.boundOnModalDeactivate) {
          window.removeEventListener('modal-trap-deactivate', this.boundOnModalDeactivate);
        }
      }

      bindStickyFilters() {
        const scrollRoot = this.closest('.swatch-tray');
        const stickyFilters = this.querySelector('.swatch-tray-filters');

        if (!(scrollRoot instanceof HTMLElement) || !(stickyFilters instanceof HTMLElement)) return;

        this.unbindStickyFilters();

        this.scrollRoot = scrollRoot;
        this.stickyFilters = stickyFilters;
        this.boundUpdateStickyFilters = () => this.updateStickyFilters();

        scrollRoot.addEventListener('scroll', this.boundUpdateStickyFilters, { passive: true });
        requestAnimationFrame(() => {
          this.updateStickyFilters();
          this.scrollActiveMotifIntoView();
        });
      }

      unbindStickyFilters() {
        this.clearStickyTransitionListener();

        if (this.scrollRoot && this.boundUpdateStickyFilters) {
          this.scrollRoot.removeEventListener('scroll', this.boundUpdateStickyFilters);
        }

        this.stickyFilters?.classList.remove('is-sticky');
        this.clearMotifScrollHints();
        this.scrollRoot = null;
        this.stickyFilters = null;
        this.boundUpdateStickyFilters = null;
      }

      updateStickyFilters() {
        const scrollRoot = this.scrollRoot;
        const stickyFilters = this.stickyFilters;

        if (!scrollRoot || !stickyFilters) return;

        if (scrollRoot.getAttribute('aria-expanded') !== 'true') {
          stickyFilters.classList.remove('is-sticky');
          return;
        }

        const styles = getComputedStyle(stickyFilters);
        const stickyOffset = Number.parseFloat(styles.insetBlockStart) || Number.parseFloat(styles.top) || 0;
        const wasStuck = stickyFilters.classList.contains('is-sticky');
        const pinned =
          stickyFilters.getBoundingClientRect().top <= scrollRoot.getBoundingClientRect().top + stickyOffset + 1;

        // Close button only: enter when pinned, stay until near top (avoids flicker on short lists).
        const isStuck = wasStuck ? scrollRoot.scrollTop > 1 : pinned && scrollRoot.scrollTop > 1;

        stickyFilters.classList.toggle('is-sticky', isStuck);

        // is-sticky animates max-inline-size; remeasure motif fades as width settles.
        if (wasStuck !== isStuck) {
          this.refreshMotifScrollHintsAfterStickyChange(stickyFilters);
        }
      }

      /**
       * Remeasure motif edge fades during/after the sticky width transition.
       * @param {HTMLElement} stickyFilters - Filter bar whose sticky width is animating
       */
      refreshMotifScrollHintsAfterStickyChange(stickyFilters) {
        this.clearStickyTransitionListener();
        requestAnimationFrame(() => this.updateMotifScrollHints());

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.boundStickyTransitionEnd = (event) => {
          if (event.target !== stickyFilters || event.propertyName !== 'max-inline-size') return;

          this.clearStickyTransitionListener();
          this.updateMotifScrollHints();
        };

        stickyFilters.addEventListener('transitionend', this.boundStickyTransitionEnd);
      }

      clearStickyTransitionListener() {
        if (this.stickyFilters && this.boundStickyTransitionEnd) {
          this.stickyFilters.removeEventListener('transitionend', this.boundStickyTransitionEnd);
        }

        this.boundStickyTransitionEnd = null;
      }

      /**
       * @returns {HTMLElement | null} Shell or motif filters element for scroll-hint classes
       */
      motifScrollHintTarget() {
        if (!(this.motifFilters instanceof HTMLElement)) return null;

        const shell = this.motifFilters.parentElement;

        if (shell instanceof HTMLElement && shell.classList.contains('swatch-tray-secondary-filters-shell')) {
          return shell;
        }

        return this.motifFilters;
      }

      clearMotifScrollHints() {
        this.motifScrollHintTarget()?.classList.remove('can-scroll-start', 'can-scroll-end');
      }

      /**
       * Show edge fades only when more motifs sit off-screen in that direction.
       */
      updateMotifScrollHints() {
        const motifFilters = this.motifFilters;
        const hintTarget = this.motifScrollHintTarget();

        if (!(motifFilters instanceof HTMLElement) || !hintTarget) return;

        if (motifFilters.hidden) {
          this.clearMotifScrollHints();
          return;
        }

        const maxScroll = motifFilters.scrollWidth - motifFilters.clientWidth;
        const tolerance = 1;

        if (maxScroll <= tolerance) {
          this.clearMotifScrollHints();
          return;
        }

        const scrollPosition = Math.abs(motifFilters.scrollLeft);

        hintTarget.classList.toggle('can-scroll-start', scrollPosition > tolerance);
        hintTarget.classList.toggle('can-scroll-end', scrollPosition < maxScroll - tolerance);
      }

      /**
       * Keep the pressed motif pill in the horizontal strip viewport.
       * Adjusts only the motif scroller so the tray body does not jump.
       */
      scrollActiveMotifIntoView() {
        const motifFilters = this.motifFilters;

        if (!(motifFilters instanceof HTMLElement) || motifFilters.hidden) {
          this.clearMotifScrollHints();
          return;
        }

        if (!this.activeMotif) {
          motifFilters.scrollLeft = 0;
          this.updateMotifScrollHints();
          return;
        }

        const activeButton = motifFilters.querySelector(
          `[data-swatch-motif-filter="${CSS.escape(this.activeMotif)}"]:not([hidden])`,
        );

        if (!(activeButton instanceof HTMLElement)) {
          this.updateMotifScrollHints();
          return;
        }

        const containerRect = motifFilters.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        const tolerance = 1;

        if (buttonRect.left < containerRect.left - tolerance) {
          motifFilters.scrollLeft += buttonRect.left - containerRect.left;
        } else if (buttonRect.right > containerRect.right + tolerance) {
          motifFilters.scrollLeft += buttonRect.right - containerRect.right;
        }

        this.updateMotifScrollHints();
      }

      /**
       * @param {boolean} expanded - Whether the swatch tray dialog is open
       */
      setTriggerExpanded(expanded) {
        if (!(this.trigger instanceof HTMLButtonElement)) return;

        this.trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }

      /**
       * Resets filters to the current PDP product's style and motif.
       * Prefers [data-current-product] over Liquid defaults so the selected
       * swatch (and checkmark) is visible when View all opens.
       * @param {{ animate?: boolean, announce?: boolean }} [options] - Filter options
       */
      resetToCurrentProductFilters({ animate = true, announce = true } = {}) {
        const currentItem = this.querySelector('[data-swatch-item][data-current-product]');
        const defaultButton =
          this.filters?.querySelector('.option-active[data-swatch-style-filter]') || this.buttons?.[0];
        const fallbackStyle =
          this.preferredStyle ||
          (defaultButton instanceof HTMLButtonElement ? defaultButton.getAttribute('data-swatch-style-filter') : null);

        if (currentItem instanceof HTMLElement) {
          const itemStyle = currentItem.getAttribute('data-swatch-style') || '';
          const itemMotif = currentItem.getAttribute('data-swatch-motif');
          const resolvedStyle = resolveStyleFromItemStyle(itemStyle);

          this.activeStyle = resolvedStyle || fallbackStyle;
          this.preferredStyle = this.activeStyle;

          // Embroidered ignores motif filters. Otherwise the current item is source of truth
          // (including null when it has no motif — do not fall back to the first motif).
          this.preferredMotif = this.activeStyle === 'embroidered' ? null : itemMotif;
        } else {
          this.activeStyle = fallbackStyle;
        }

        this.syncMotifFilters({ preferCurrentProduct: true });
        this.applyFilter({ animate, announce });
      }

      /**
       * @param {HTMLButtonElement} button - The style filter button that was clicked
       */
      toggleFilter(button) {
        const style = button.getAttribute('data-swatch-style-filter');
        if (!style || style === this.activeStyle) return;

        this.activeStyle = style;
        this.syncMotifFilters({ preferCurrentProduct: false });
        this.applyFilter();
        this.scrollTrayToTop();
      }

      /**
       * Reset tray scroll so sticky close control hides after a primary filter change.
       */
      scrollTrayToTop() {
        const scrollRoot = this.scrollRoot;

        if (!scrollRoot) return;

        scrollRoot.scrollTop = 0;
        this.updateStickyFilters();
      }

      /**
       * @param {HTMLButtonElement} button - The motif filter button that was clicked
       */
      toggleMotifFilter(button) {
        const motif = button.getAttribute('data-swatch-motif-filter');
        if (!motif || motif === this.activeMotif) return;

        this.activeMotif = motif;
        this.applyFilter();
      }

      /**
       * @param {{ animate?: boolean, announce?: boolean }} [options] - Filter options
       */
      applyFilter({ animate = true, announce = true } = {}) {
        const activeStyle = this.activeStyle;
        if (!activeStyle) return;

        this.buttons?.forEach((filterButton) => {
          const isActive = filterButton.getAttribute('data-swatch-style-filter') === activeStyle;
          filterButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          filterButton.classList.toggle('option-active', isActive);
        });

        this.motifButtons?.forEach((motifButton) => {
          if (!(motifButton instanceof HTMLButtonElement)) return;

          const isForActiveStyle = motifButton.getAttribute('data-swatch-motif-style') === activeStyle;
          const isActive =
            isForActiveStyle && motifButton.getAttribute('data-swatch-motif-filter') === this.activeMotif;

          motifButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          motifButton.classList.toggle('option-active', isActive);
        });

        this.updateIndicator({ animate });

        let visibleCount = 0;

        this.items?.forEach((item) => {
          if (!(item instanceof HTMLElement)) return;

          const itemStyle = item.getAttribute('data-swatch-style') || '';
          const itemMotif = item.getAttribute('data-swatch-motif') || '';
          const styleMatch = itemStyle.includes(activeStyle);
          const motifMatch = this.activeMotif === null || itemMotif === this.activeMotif;
          const isMatch = styleMatch && motifMatch;

          item.hidden = !isMatch;
          item.setAttribute('data-active', isMatch ? 'true' : 'false');

          if (isMatch) visibleCount += 1;
        });

        if (announce) {
          this.announceResults(visibleCount);
        }
      }

      /**
       * @param {number} count - Number of visible swatch results
       */
      announceResults(count) {
        if (!(this.status instanceof HTMLElement)) return;

        const templateKey = count === 1 ? 'data-results-count-one' : 'data-results-count-other';
        const template = this.getAttribute(templateKey) || '__COUNT__';

        this.status.textContent = template.replace(/__COUNT__/g, String(count));
      }

      /**
       * @param {{ preferCurrentProduct?: boolean }} [options] - Motif sync options
       */
      syncMotifFilters({ preferCurrentProduct = false } = {}) {
        if (!(this.motifFilters instanceof HTMLElement)) {
          this.activeMotif = null;
          return;
        }

        // Embroidered never shows secondary motif filters — all embroidered products are shown.
        if (this.activeStyle === 'embroidered') {
          this.motifButtons?.forEach((motifButton) => {
            if (!(motifButton instanceof HTMLButtonElement)) return;
            motifButton.hidden = true;
          });
          this.motifFilters.hidden = true;
          this.activeMotif = null;
          this.clearMotifScrollHints();
          return;
        }

        /** @type {string | null} */
        let firstVisibleMotif = null;
        let hasVisibleMotifs = false;
        let preferredMotifIsVisible = false;

        this.motifButtons?.forEach((motifButton) => {
          if (!(motifButton instanceof HTMLButtonElement)) return;

          const isForActiveStyle = motifButton.getAttribute('data-swatch-motif-style') === this.activeStyle;
          motifButton.hidden = !isForActiveStyle;

          if (!isForActiveStyle) return;

          hasVisibleMotifs = true;

          const motif = motifButton.getAttribute('data-swatch-motif-filter');

          if (!firstVisibleMotif) {
            firstVisibleMotif = motif;
          }

          if (preferCurrentProduct && this.preferredMotif && motif === this.preferredMotif) {
            preferredMotifIsVisible = true;
          }
        });

        this.motifFilters.hidden = !hasVisibleMotifs;

        if (!hasVisibleMotifs) {
          this.activeMotif = null;
          this.clearMotifScrollHints();
          return;
        }

        if (preferCurrentProduct) {
          if (preferredMotifIsVisible) {
            this.activeMotif = this.preferredMotif;
            requestAnimationFrame(() => this.scrollActiveMotifIntoView());
            return;
          }

          // Current product has no motif for this style — show all matching style
          // so the selected swatch stays visible instead of forcing the first motif.
          if (!this.preferredMotif) {
            this.activeMotif = null;
            requestAnimationFrame(() => this.scrollActiveMotifIntoView());
            return;
          }
        }

        this.activeMotif = firstVisibleMotif;
        requestAnimationFrame(() => this.scrollActiveMotifIntoView());
      }

      /**
       * @param {{ animate?: boolean }} [options] - Indicator animation options
       */
      updateIndicator({ animate = true } = {}) {
        if (!(this.filters instanceof HTMLElement)) return;

        const activeButton = this.filters.querySelector('.option-active');
        if (!(activeButton instanceof HTMLElement)) return;

        const containerRect = this.filters.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        if (containerRect.width === 0 || buttonRect.width === 0) return;

        if (!animate) {
          this.filters.classList.remove('is-animated');
        }

        const sizePercent = (buttonRect.width / containerRect.width) * 100;
        const offsetPercent = ((buttonRect.left - containerRect.left) / containerRect.width) * 100;

        this.filters.style.setProperty('--swatch-tray-indicator-inline-size', `${sizePercent}%`);
        this.filters.style.setProperty('--swatch-tray-indicator-offset', `${offsetPercent}%`);
        this.filters.classList.add('is-ready');

        if (!animate) {
          // Commit the snapped position before re-enabling transitions.
          void this.filters.offsetWidth;
          this.filters.classList.add('is-animated');
          return;
        }

        this.filters.classList.add('is-animated');
      }
    },
  );
}
